/**
 * Vite plugin for multiplayer game development.
 *
 * - `closeBundle`: Builds the server entry via esbuild → `dist/server-bundle.js`
 *   and copies `rooms.config.json` to `dist/`.
 * - `configureServer`: Starts a local WS room server on port 9001 with
 *   esbuild watch + hot-reload for `vite dev`.
 */

import type { Plugin } from 'vite'
import fs from 'fs'
import path from 'path'
import type { DevServerOptions, DeterministicRoomClass } from './dev-server'
import type { PlatformServices } from '../../mp/server'
import { resolveRoomsConfigPath } from './configPath'
import { assertWireShape, stampRuntimePin, type SyncplayRuntimePin } from './stampRuntimePin'

export type { DeterministicRoomClass } from './dev-server'

export interface MultiplayerPluginOptions {
  /** Path to rooms.config.json (default: 'rooms.config.json' in project root) */
  configPath?: string
  /** Port for local dev WS server (default: 9001) */
  devPort?: number
  /** Whether to run dev rooms in Worker Threads (default: false) */
  threaded?: boolean
  /** Max bundle size in bytes (default: 5MB) */
  maxBundleSize?: number
  /** Per-sub-service overrides for the dev MockPlatformServices. */
  mockServices?: Partial<PlatformServices>
  /** Static object returned by `services.getGameConfig()` in dev. */
  gameConfig?: Record<string, unknown>
  /** Class for `deterministic: true` rooms — pass `DevSyncplayAuthorityRoom`
   *  from `@series-inc/rundot-syncplay/node`. Omit for non-deterministic games. */
  deterministicRoomClass?: DeterministicRoomClass
  /**
   * The deterministic runtime this build simulates, from
   * `createSyncplayRuntimePin()` in `@series-inc/rundot-syncplay/node`. When
   * supplied, the plugin stamps it into `dist/rooms.config.json`, binding every
   * published room to these rules — the platform then rejects a join ticket
   * that asks for different ones.
   */
  syncplayRuntime?: SyncplayRuntimePin
}

const MAX_BUNDLE_SIZE_DEFAULT = 5 * 1024 * 1024 // 5MB
const DEFAULT_DEV_PORT = 9001

export function rundotMultiplayerPlugin(options: MultiplayerPluginOptions = {}): Plugin {
  const maxBundleSize = options.maxBundleSize ?? MAX_BUNDLE_SIZE_DEFAULT
  let projectRoot = ''
  let resolvedConfigPath = 'rooms.config.json' // overwritten in configResolved once projectRoot is known
  let devServerClose: (() => Promise<void>) | null = null
  let isServe = false

  return {
    name: 'rundot-multiplayer',

    configResolved(config) {
      projectRoot = config.root
      isServe = config.command === 'serve'
      resolvedConfigPath = options.configPath ?? resolveRoomsConfigPath(projectRoot)
      // Validate here, not in closeBundle: that hook catches everything and only
      // console.errors, so a throw there would print and still ship a bundle.
      if (options.syncplayRuntime !== undefined) {
        assertWireShape(options.syncplayRuntime)
      }
    },

    async configureServer(server) {
      // Start local WS room server for development
      const devOptions: DevServerOptions = {
        port: options.devPort ?? DEFAULT_DEV_PORT,
        threaded: options.threaded ?? false,
        configPath: path.resolve(projectRoot, resolvedConfigPath),
        projectRoot,
        mockServices: options.mockServices,
        gameConfig: options.gameConfig,
        deterministicRoomClass: options.deterministicRoomClass,
        syncplayRuntime: options.syncplayRuntime,
      }

      const { startDevServer } = await import('./dev-server')
      const devServer = startDevServer(server, devOptions)
      devServerClose = devServer.close

      // Clean up on server close
      server.httpServer?.on('close', () => {
        devServer.close()
      })
    },

    transformIndexHtml() {
      if (!isServe) return []
      const port = options.devPort ?? DEFAULT_DEV_PORT
      return [
        {
          tag: 'script' as const,
          attrs: { type: 'module' },
          children: `window.__RUNDOT_MULTIPLAYER_DEV_SERVER__ = "http://localhost:${port}";`,
          injectTo: 'head-prepend' as const,
        },
      ]
    },

    async closeBundle() {
      const configFullPath = path.resolve(projectRoot, resolvedConfigPath)

      // Skip if no rooms.config.json (not a multiplayer game)
      if (!fs.existsSync(configFullPath)) {
        return
      }

      try {
        const configContent = fs.readFileSync(configFullPath, 'utf-8')
        const roomsConfig = JSON.parse(configContent)

        if (!roomsConfig.rooms || roomsConfig.rooms.length === 0) {
          console.warn('[RUN:multiplayer] rooms.config.json has no rooms defined, skipping server build')
          return
        }

        // Dynamically import esbuild (it's a peer/dev dependency)
        const esbuild = await import('esbuild')

        // Determine entry points from rooms.config.json
        // We create a virtual entry that re-exports all room classes.
        // Deterministic (Syncplay) rooms have NO `file` — the platform runs its
        // built-in input authority — so they contribute nothing to the bundle
        // but must still be carried through to dist/rooms.config.json below.
        const bundledRooms = roomsConfig.rooms.filter(
          (room: { file?: string }) => typeof room.file === 'string' && room.file.length > 0,
        )
        const entryContent = bundledRooms
          .map((room: { type: string; file: string; export?: string }, i: number) => {
            const exportName = room.export || 'default'
            // Ensure relative paths start with ./ so esbuild doesn't treat them as packages
            const filePath = room.file.startsWith('.') ? room.file : `./${room.file}`
            if (exportName === 'default') {
              return `export { default as __Room_${i}__ } from '${filePath}';`
            }
            return `export { ${exportName} as __Room_${i}__ } from '${filePath}';`
          })
          .join('\n')

        // Resolved dist config: bundled rooms are repointed at their virtual
        // entry export; file-less (deterministic) rooms pass through untouched.
        let bundledIndex = 0
        const distConfig = {
          ...roomsConfig,
          rooms: roomsConfig.rooms.map(
            (room: { type: string; file?: string; export?: string; config?: Record<string, unknown> }) => {
              if (typeof room.file !== 'string' || room.file.length === 0) {
                return room
              }
              const { file: _file, ...rest } = room
              return { ...rest, export: `__Room_${bundledIndex++}__` }
            },
          ),
        }
        const writeDistConfig = (): void => {
          fs.writeFileSync(
            path.resolve(projectRoot, 'dist/rooms.config.json'),
            JSON.stringify(stampRuntimePin(distConfig, options.syncplayRuntime), null, 2),
          )
        }

        if (bundledRooms.length === 0) {
          // Purely deterministic game: no creator server code to bundle, but the
          // rooms config must still ship.
          writeDistConfig()
          return
        }

        // Write temporary entry file
        const tmpEntryPath = path.resolve(projectRoot, '.rundot-server-entry.tmp.ts')
        fs.writeFileSync(tmpEntryPath, entryContent)

        try {
          const result = await esbuild.build({
            entryPoints: [tmpEntryPath],
            bundle: true,
            format: 'cjs',
            platform: 'neutral', // No Node.js built-ins
            target: 'es2022',
            outfile: path.resolve(projectRoot, 'dist/server-bundle.js'),
            sourcemap: false,
            minify: true,
            treeShaking: true,
            // Block Node.js built-ins
            external: [],
            plugins: [
              // Import the blocklist plugin
              (await import('./esbuild-blocklist')).esbuildBlocklistPlugin(),
              (await import('./esbuild-validate')).esbuildValidatePlugin(projectRoot),
            ],
          })

          if (result.errors.length > 0) {
            console.error('[RUN:multiplayer] Server bundle build failed:')
            for (const err of result.errors) {
              console.error(`  ${err.text}`)
            }
            return
          }

          // Check bundle size
          const bundlePath = path.resolve(projectRoot, 'dist/server-bundle.js')
          const stat = fs.statSync(bundlePath)
          if (stat.size > maxBundleSize) {
            console.error(
              `[RUN:multiplayer] Server bundle too large: ${(stat.size / 1024 / 1024).toFixed(2)}MB ` +
              `(limit: ${(maxBundleSize / 1024 / 1024).toFixed(2)}MB)`,
            )
            return
          }

          // Write updated rooms.config.json to dist/ with resolved export names
          writeDistConfig()
        } finally {
          // Clean up temp file
          if (fs.existsSync(tmpEntryPath)) {
            fs.unlinkSync(tmpEntryPath)
          }
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err)
        console.error(`[RUN:multiplayer] Server bundle build error: ${message}`)
      }
    },

    async buildEnd() {
      if (devServerClose) {
        await devServerClose()
        devServerClose = null
      }
    },
  }
}
